import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaChevronDown, FaSearch } from "react-icons/fa";
import styles from "./CustomSelect.module.css";

const CustomSelect = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = "Chọn một lựa chọn", 
  className = "",
  disabled = false,
  searchable = true 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Find selected option
  const selectedOption = options.find(option => option.value === value);

  const handleOptionSelect = useCallback((option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
  }, [onChange]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0) {
            handleOptionSelect(filteredOptions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSearchTerm("");
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, highlightedIndex, filteredOptions, handleOptionSelect]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedIndex(-1);
  };

  return (
    <div 
      ref={selectRef}
      className={`${styles.customSelect} ${className} ${disabled ? styles.disabled : ''}`}
    >
      {/* Select Trigger */}
      <div 
        className={styles.selectTrigger}
        onClick={handleToggle}
      >
        <div className={styles.selectValue}>
          {selectedOption ? (
            <span className={styles.selectedLabel}>{selectedOption.label}</span>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <FaChevronDown className={`${styles.chevron} ${isOpen ? styles.open : ''}`} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className={styles.dropdown}>
          {searchable && (
            <div className={styles.searchContainer}>
              <FaSearch className={styles.searchIcon} />
              <input
                ref={inputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Tìm kiếm..."
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className={styles.optionsContainer}>
            {filteredOptions.length === 0 ? (
              <div className={styles.noOptions}>Không tìm thấy kết quả</div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={`${styles.option} ${
                    option.value === value ? styles.selected : ''
                  } ${index === highlightedIndex ? styles.highlighted : ''}`}
                  onClick={() => handleOptionSelect(option)}
                >
                  <span className={styles.optionLabel}>{option.label}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
